import { Injectable } from '@angular/core'
import { hasValue } from '@util-lib/hasValue'
import { generateFaqData } from './data/faq.data'
import {
    FAQCategory,
    FAQData,
    FAQQuestion,
    FAQTopic,
    FAQTopicKey,
} from './faq.model'
import { FaqValidator, getAllFaqValidators } from './faq.validator'

@Injectable({
    providedIn: 'root',
})
export class FaqService {
    private readonly data: FAQData
    private readonly validators: FaqValidator[]

    constructor() {
        this.validators = getAllFaqValidators()
        this.data = this.validate(generateFaqData())
    }

    /*
     * Checks the faq data by collecting all error messages from all faq
     * validators and throws an error in case of at least an error message.
     * This happens also during the test run and should ensure data
     * consistency.
     */
    private validate(data: FAQData): FAQData {
        const errors: string[] = this.validators
            .map((validator) => validator(data))
            .filter(hasValue)
        if (errors.length === 0) {
            return data
        } else {
            throw new Error(`FAQ data validation failed: ${errors.join(',')}`)
        }
    }

    getTopicByKey(topicKey: FAQTopicKey): FAQTopic | undefined {
        return this.data.topics.find((topic) => topic.topicKey === topicKey)
    }

    getCategoryByKey(categoryKey: string): FAQCategory | undefined {
        return this.data.topics
            .flatMap((topic) => topic.categories)
            .find((category) => category.categoryKey === categoryKey)
    }

    getQuestionByKey(questionKey: string): FAQQuestion | null {
        return this.data.topics
            .flatMap((topic) => topic.categories)
            .flatMap((category) => category.questions)[questionKey]
    }
}
